import React, { useState, useEffect } from 'react';
import SpecialistDashboardLayout from '../../components/SpecialistDashboardLayout';
import VerificationBadgeModal from '../../components/VerificationBadgeModal/VerificationBadgeModal';
import PaymentGatewayModal from '../../components/PaymentGatewayModal/PaymentGatewayModal';
import { useSpecialistAuth } from '../../context/SpecialistAuthContext';
import membershipService from '../../services/membershipService';
import { useCurrency } from '../../context/CurrencyContext';
import '../MembershipPackagesPage/MembershipPackagesPage.css';

const SpecialistMembershipPage = () => {
  const { specialist } = useSpecialistAuth();
  const { formatPrice, selectedCurrency } = useCurrency();
  const [billingPeriod, setBillingPeriod] = useState('annual');
  const [plans, setPlans] = useState([]);
  const [badges, setBadges] = useState([]);
  const [userSubscription, setUserSubscription] = useState(null);
  const [userBadges, setUserBadges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(false);
  const [purchasingBadge, setPurchasingBadge] = useState(false);

  const [selectedBadge, setSelectedBadge] = useState(null);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [uploadedDocuments, setUploadedDocuments] = useState(null);

  // Default plans as per Figma design
  const defaultPlans = [
    {
      id: 1,
      name: 'basic',
      displayName: 'Basic',
      description: 'For Exploring the platform',
      monthlyPrice: 0,
      annualPrice: 0,
      originalPrice: 0,
      savings: 0,
      isHighlighted: true,
      features: {
        productPostingLimit: 10,
        canReceiveRFQs: true,
        rfqRegion: 'global',
        canViewBuyerContacts: false,
        buyerContactRegion: null,
        canReceiveDirectCalls: false,
        buyerDirectoryAccess: false,
        buyerDirectoryRegion: null,
        canUploadVideos: false,
        maxProductImages: 5,
        hasCompanyProfile: true,
        canLiveChat: false,
        canHighlightBrand: false,
        hasDashboardAccess: true,
        canAddProfessional: false,
        canSubmitProjects: false,
        canPostNews: false,
        trustedBadgeAvailable: false
      }
    },
    {
      id: 2,
      name: 'gcc-business',
      displayName: 'GCC Business',
      description: 'for Gulf-based companies',
      monthlyPrice: 49,
      annualPrice: 49,
      originalPrice: 49,
      savings: 250,
      features: {
        productPostingLimit: 100,
        canReceiveRFQs: true,
        rfqRegion: 'gcc',
        canViewBuyerContacts: true,
        buyerContactRegion: 'gcc',
        canReceiveDirectCalls: true,
        buyerDirectoryAccess: true,
        buyerDirectoryRegion: 'gcc',
        canUploadVideos: true,
        maxProductImages: 5,
        hasCompanyProfile: true,
        canLiveChat: true,
        canHighlightBrand: true,
        hasDashboardAccess: true,
        canAddProfessional: true,
        canSubmitProjects: true,
        canPostNews: true,
        trustedBadgeAvailable: 'extra'
      }
    },
    {
      id: 3,
      name: 'global-enterprise',
      displayName: 'Global Enterprise',
      description: 'For Global companies (Gulf included)',
      monthlyPrice: 99,
      annualPrice: 99,
      originalPrice: 149,
      savings: 250,
      features: {
        productPostingLimit: 200,
        canReceiveRFQs: true,
        rfqRegion: 'global',
        canViewBuyerContacts: true,
        buyerContactRegion: 'all',
        canReceiveDirectCalls: true,
        buyerDirectoryAccess: true,
        buyerDirectoryRegion: 'all',
        canUploadVideos: true,
        maxProductImages: 5,
        hasCompanyProfile: true,
        canLiveChat: true,
        canHighlightBrand: true,
        hasDashboardAccess: true,
        canAddProfessional: true,
        canSubmitProjects: true,
        canPostNews: true,
        trustedBadgeAvailable: 'extra'
      }
    }
  ];

  const defaultBadges = [
    {
      id: 1,
      name: 'verified',
      displayName: 'Verified',
      description: 'Company documents reviewed and validated.',
      price: 99,
      icon: 'verified',
      isActive: true
    },
    {
      id: 2,
      name: 'inspected',
      displayName: 'Inspected',
      description: 'On-site inspection with photo and Video documentation',
      price: 249,
      icon: 'inspected',
      isActive: false
    },
    {
      id: 3,
      name: 'certified',
      displayName: 'Certified',
      description: 'Documents + on-site visit + client review. Full trust package.',
      price: 499,
      icon: 'certified',
      isActive: false
    }
  ];

  useEffect(() => {
    fetchData();

    const handleFocus = () => {
      fetchData();
    };
    window.addEventListener('focus', handleFocus);
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [specialist]);

  const fetchData = async () => {
    setLoading(true);
    try {
      try {
        const plansData = await membershipService.getPlans();
        if (plansData && plansData.length > 0) {
          const mergedPlans = plansData.map(apiPlan => {
            const defaultPlan = defaultPlans.find(dp => dp.name === apiPlan.name);
            const fallbackFeatures = defaultPlan?.features || defaultPlans[0].features;
            return {
              id: apiPlan.id,
              name: apiPlan.name,
              displayName: apiPlan.displayName || apiPlan.name,
              description: apiPlan.description || '',
              monthlyPrice: apiPlan.monthlyPrice ?? 0,
              annualPrice: apiPlan.annualPrice ?? 0,
              features: {
                productPostingLimit: apiPlan.features?.productPostingLimit ?? fallbackFeatures.productPostingLimit,
                canReceiveRFQs: apiPlan.features?.canReceiveRFQs ?? fallbackFeatures.canReceiveRFQs,
                rfqRegion: apiPlan.features?.rfqRegion ?? fallbackFeatures.rfqRegion,
                canViewBuyerContacts: apiPlan.features?.canViewBuyerContacts ?? fallbackFeatures.canViewBuyerContacts,
                buyerContactRegion: apiPlan.features?.buyerContactRegion ?? fallbackFeatures.buyerContactRegion,
                canReceiveDirectCalls: apiPlan.features?.canReceiveDirectCalls ?? fallbackFeatures.canReceiveDirectCalls,
                buyerDirectoryAccess: apiPlan.features?.buyerDirectoryAccess ?? fallbackFeatures.buyerDirectoryAccess,
                buyerDirectoryRegion: apiPlan.features?.buyerDirectoryRegion ?? fallbackFeatures.buyerDirectoryRegion,
                canUploadVideos: apiPlan.features?.canUploadVideos ?? fallbackFeatures.canUploadVideos,
                maxProductImages: apiPlan.features?.maxProductImages ?? fallbackFeatures.maxProductImages,
                hasCompanyProfile: apiPlan.features?.hasCompanyProfile ?? fallbackFeatures.hasCompanyProfile,
                canLiveChat: apiPlan.features?.canLiveChat ?? fallbackFeatures.canLiveChat,
                canHighlightBrand: apiPlan.features?.canHighlightBrand ?? fallbackFeatures.canHighlightBrand,
                hasDashboardAccess: apiPlan.features?.hasDashboardAccess ?? fallbackFeatures.hasDashboardAccess,
                canAddProfessional: apiPlan.features?.canAddProfessional ?? fallbackFeatures.canAddProfessional,
                canSubmitProjects: apiPlan.features?.canSubmitProjects ?? fallbackFeatures.canSubmitProjects,
                canPostNews: apiPlan.features?.canPostNews ?? fallbackFeatures.canPostNews,
                trustedBadgeAvailable: apiPlan.features?.trustedBadgeAvailable ?? fallbackFeatures.trustedBadgeAvailable
              }
            };
          });
          setPlans(mergedPlans);
        } else {
          setPlans(defaultPlans);
        }
      } catch (error) {
        console.error('Error fetching plans from API, using defaults:', error);
        setPlans(defaultPlans);
      }

      try {
        const badgesData = await membershipService.getBadges();
        setBadges(badgesData.length > 0 ? badgesData : defaultBadges);
      } catch {
        setBadges(defaultBadges);
      }

      if (specialist?.id) {
        try {
          const subData = await membershipService.getUserSubscription(specialist.id);
          setUserSubscription(subData);
        } catch {
          setUserSubscription({ plan: { name: 'basic' }, status: 'free' });
        }

        try {
          const userBadgesData = await membershipService.getUserBadges(specialist.id);
          setUserBadges(userBadgesData);
        } catch {
          setUserBadges([]);
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (planId) => {
    if (!specialist?.id) {
      alert('Please login to subscribe to a plan');
      return;
    }
    setSubscribing(true);
    try {
      await membershipService.subscribe(specialist.id, planId, billingPeriod);
      await fetchData();
      alert('Successfully subscribed to the plan!');
    } catch (error) {
      console.error('Error subscribing:', error);
      alert('Failed to subscribe: ' + (error.response?.data?.error || error.message || 'Please try again.'));
    } finally {
      setSubscribing(false);
    }
  };

  const handlePurchaseBadge = async (badgeId) => {
    if (!specialist?.id) {
      alert('Please login to purchase a badge');
      return;
    }
    const badge = badges.find(b => b.id === badgeId);
    if (badge) {
      setSelectedBadge(badge);
      setShowVerificationModal(true);
    }
  };

  const handleVerificationSubmit = (documents) => {
    setUploadedDocuments(documents);
    setShowVerificationModal(false);
    setShowPaymentModal(true);
  };

  const handlePaymentConfirm = async () => {
    if (!specialist?.id || !selectedBadge) return;
    setPurchasingBadge(true);
    try {
      await membershipService.purchaseBadge(specialist.id, selectedBadge.id);
      await fetchData();
      setShowPaymentModal(false);
      setSelectedBadge(null);
      setUploadedDocuments(null);
      alert('Successfully purchased the badge!');
    } catch (error) {
      console.error('Error purchasing badge:', error);
      alert('Failed to purchase badge. Please try again.');
    } finally {
      setPurchasingBadge(false);
    }
  };

  const handleCloseModals = () => {
    setShowVerificationModal(false);
    setShowPaymentModal(false);
    setSelectedBadge(null);
    setUploadedDocuments(null);
  };

  const getCurrentPlanName = () => {
    if (userSubscription?.plan?.name) {
      return userSubscription.plan.name;
    }
    return 'basic';
  };

  const isUserBadgeActive = (badgeName) => {
    return userBadges.some(ub => ub.badge?.name === badgeName && ub.status === 'active');
  };

  const getPrice = (plan) => {
    if (plan.monthlyPrice === 0) return 'Free';
    const price = billingPeriod === 'annual' ? plan.annualPrice : plan.monthlyPrice;
    return `${formatPrice(price)}/`;
  };

  const getPricePeriod = (plan) => {
    if (plan.monthlyPrice === 0) return '';
    return 'month';
  };

  const features = [
    {
      name: 'Product / Service Posting Limit',
      getValue: (plan) => plan.features?.productPostingLimit?.toString() || '10'
    },
    {
      name: 'Receive Buy Leads / RFQs',
      getValue: (plan) => {
        if (!plan.features?.canReceiveRFQs) return { type: 'x' };
        const region = plan.features.rfqRegion;
        return { type: 'check', text: region === 'gcc' ? 'Only GCC' : 'Global' };
      }
    },
    {
      name: 'View Buyer Contact Details',
      getValue: (plan) => {
        if (!plan.features?.canViewBuyerContacts) return { type: 'x' };
        const region = plan.features.buyerContactRegion;
        return { type: 'check', text: region === 'gcc' ? '(GCC Buyers)' : '(All Buyers)' };
      }
    },
    {
      name: 'Receive Direct Calls from Buyers',
      getValue: (plan) => plan.features?.canReceiveDirectCalls ? { type: 'check' } : { type: 'x' }
    },
    {
      name: 'Buyer Directory Access',
      getValue: (plan) => {
        if (!plan.features?.buyerDirectoryAccess) return { type: 'x' };
        const region = plan.features.buyerDirectoryRegion;
        return { type: 'check', text: region === 'gcc' ? '(GCC Buyers)' : '(All Buyers)' };
      }
    },
    {
      name: 'Upload Videos',
      getValue: (plan) => plan.features?.canUploadVideos ? { type: 'check' } : { type: 'x' }
    },
    {
      name: 'Max Product Images per Listing',
      getValue: (plan) => plan.features?.maxProductImages?.toString() || '5'
    },
    {
      name: 'Company Profile with Logo',
      getValue: (plan) => plan.features?.hasCompanyProfile ? { type: 'check' } : { type: 'x' }
    },
    {
      name: 'Live Chat with Buyers',
      getValue: (plan) => plan.features?.canLiveChat ? { type: 'check' } : { type: 'x' }
    },
    {
      name: 'Highlight Your Brand (Optional)',
      getValue: (plan) => plan.features?.canHighlightBrand ? { type: 'check' } : { type: 'x' }
    },
    {
      name: 'Specialist Dashboard Access',
      getValue: (plan) => plan.features?.hasDashboardAccess ? { type: 'check' } : { type: 'x' }
    },
    {
      name: 'Add Profile to Professionals Section',
      getValue: (plan) => plan.features?.canAddProfessional ? { type: 'check' } : { type: 'x' }
    },
    {
      name: 'Submit Projects in Projects Section',
      getValue: (plan) => plan.features?.canSubmitProjects ? { type: 'check' } : { type: 'x' }
    },
    {
      name: 'Post News in News Section',
      getValue: (plan) => plan.features?.canPostNews ? { type: 'check' } : { type: 'x' }
    },
    {
      name: 'Trusted Specialist Badge ( Add-on)',
      getValue: (plan) => {
        if (!plan.features?.trustedBadgeAvailable) return { type: 'x' };
        if (plan.features?.trustedBadgeAvailable === 'extra') return { type: 'text', text: 'Available with Extra Fee' };
        return { type: 'text', text: 'Available with Extra Fee' };
      }
    }
  ];

  const renderCheckIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M19 3H5C3.89543 3 3 3.89543 3 5V19C3 20.1046 3.89543 21 5 21H19C20.1046 21 21 20.1046 21 19V5C21 3.89543 20.1046 3 19 3Z" fill="#009D0A" stroke="#009D0A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M17 8.5L10.125 15.5L7 12.3182" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  const renderXIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M18 6L6 18" stroke="#FF0000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M6 6L18 18" stroke="#FF0000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  const renderFeatureValue = (value) => {
    if (typeof value === 'string') {
      return <span className="mp-feature-text">{value}</span>;
    }
    if (value.type === 'check') {
      return (
        <div className="mp-feature-check">
          {renderCheckIcon()}
          {value.text && <span className="mp-feature-label">{value.text}</span>}
        </div>
      );
    }
    if (value.type === 'x') {
      return <div className="mp-feature-x">{renderXIcon()}</div>;
    }
    if (value.type === 'text') {
      return <span className="mp-feature-small">{value.text}</span>;
    }
    return null;
  };

  if (loading) {
    return (
      <SpecialistDashboardLayout>
        <div className="mp-container">
          <div className="mp-loading">
            <div className="mp-spinner"></div>
            <p>Loading membership packages...</p>
          </div>
        </div>
      </SpecialistDashboardLayout>
    );
  }

  const currentPlanName = getCurrentPlanName();
  const displayPlans = plans.length > 0 ? plans : defaultPlans;
  const displayBadges = badges.length > 0 ? badges : defaultBadges;

  const currentPlan = displayPlans.find(p => p.name === currentPlanName) || displayPlans[0];
  const currentProductLimit = currentPlan?.features?.productPostingLimit || 10;
  const currentPlanDisplay = currentPlan?.displayName || 'Basic';

  return (
    <SpecialistDashboardLayout>
      <div className="mp-container">
        {/* Current Plan Status Banner */}
        {specialist && (
          <div className="mp-current-plan-banner">
            <div className="mp-current-plan-info">
              <span className="mp-current-plan-label">Your Current Plan:</span>
              <span className={`mp-current-plan-name ${currentPlanName}`}>{currentPlanDisplay}</span>
            </div>
            <div className="mp-current-plan-limits">
              <div className="mp-limit-item">
                <span className="mp-limit-icon">📦</span>
                <span className="mp-limit-text">Product Limit: <strong>{currentProductLimit}</strong></span>
              </div>
              <div className="mp-limit-item">
                <span className="mp-limit-icon">🖼️</span>
                <span className="mp-limit-text">Images per Product: <strong>{currentPlan?.features?.maxProductImages || 5}</strong></span>
              </div>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="mp-header">
          <h1 className="mp-title">Membership Packages</h1>
          <p className="mp-subtitle">Flexible plans designed for suppliers, buyers, and professionals at every level.</p>
        </div>

        {/* Billing Toggle Row */}
        <div style={{ display: 'flex', gap: '16px', marginBottom: '8px', alignItems: 'center' }}>
          <div style={{ width: '437px', minWidth: '437px', flexShrink: 0 }}></div>
          <div style={{ flex: 1 }}></div>
          <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <div className="mp-billing-toggle">
              <span className={`mp-billing-label ${billingPeriod === 'monthly' ? 'active' : ''}`}>Monthly</span>
              <button
                className={`mp-billing-switch ${billingPeriod === 'annual' ? 'annual' : ''}`}
                onClick={() => setBillingPeriod(billingPeriod === 'monthly' ? 'annual' : 'monthly')}
                type="button"
              >
                <span className="mp-switch-knob"></span>
              </button>
              <span className={`mp-billing-label ${billingPeriod === 'annual' ? 'active' : ''}`}>Annual</span>
              {billingPeriod === 'annual' && (() => {
                const paidPlans = displayPlans.filter(p => p.monthlyPrice > 0);
                if (paidPlans.length === 0) return null;
                const avgDiscount = Math.round(
                  paidPlans.reduce((sum, p) => {
                    const yearlyIfMonthly = p.monthlyPrice * 12;
                    const discount = yearlyIfMonthly > 0 ? ((yearlyIfMonthly - p.annualPrice) / yearlyIfMonthly) * 100 : 0;
                    return sum + discount;
                  }, 0) / paidPlans.length
                );
                return avgDiscount > 0 ? <span className="mp-discount-badge">{avgDiscount}% off</span> : null;
              })()}
            </div>
          </div>
          <div style={{ flex: 1 }}></div>
        </div>

        {/* Main Card */}
        <div className="mp-main-card">
          {/* Plans Header */}
          <div className="mp-plans-row">
            <div className="mp-label-cell"></div>
            {displayPlans.map((plan) => {
              const isCurrent = currentPlanName === plan.name;
              const isBasic = plan.name === 'basic';

              const displayPrice = billingPeriod === 'annual'
                ? plan.annualPrice
                : plan.monthlyPrice;
              const annualSavings = billingPeriod === 'annual' && plan.monthlyPrice > 0
                ? Math.round((plan.monthlyPrice * 12) - plan.annualPrice)
                : 0;

              return (
                <div key={plan.id} className={`mp-plan-card ${isBasic ? 'basic' : ''} ${isCurrent ? 'current-plan' : ''}`}>
                  <div className="mp-plan-content">
                    <div className="mp-plan-info">
                      <h3 className="mp-plan-title">{plan.displayName}</h3>
                      <p className="mp-plan-desc">{plan.description}</p>
                    </div>
                    <div className="mp-plan-pricing">
                      {plan.monthlyPrice === 0 ? (
                        <span className="mp-plan-price free">Free</span>
                      ) : (
                        <div className="mp-plan-price-section">
                          <div className="mp-price-row">
                            <div className="mp-price-main">
                              <span className="mp-plan-price">{formatPrice(displayPrice)}/</span>
                              <span className="mp-plan-period">{billingPeriod === 'annual' ? 'year' : 'month'}</span>
                            </div>
                          </div>
                          {billingPeriod === 'annual' && annualSavings > 0 && (
                            <div className="mp-save-badge">Save {formatPrice(annualSavings)}</div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <button
                    className={`mp-plan-btn ${isCurrent ? 'current' : ''} ${isBasic && !isCurrent ? 'basic-btn' : ''}`}
                    onClick={() => {
                      if (!isCurrent) {
                        handleSubscribe(plan.id);
                      }
                    }}
                    disabled={isCurrent || subscribing}
                  >
                    {subscribing ? 'Processing...' : isCurrent ? 'Current Plan' : isBasic ? 'Switch to Basic' : 'Upgrade Plan'}
                  </button>
                </div>
              );
            })}
          </div>

          {/* Features Table */}
          <div className="mp-features-table">
            {features.map((feature, index) => (
              <div key={index} className={`mp-feature-row ${index === 0 ? 'first-row' : ''}`}>
                <div className="mp-feature-name-cell">
                  <span>{feature.name}</span>
                </div>
                <div className="mp-feature-values-wrapper">
                  {displayPlans.map((plan) => {
                    const isCurrent = currentPlanName === plan.name;
                    return (
                      <div
                        key={plan.id}
                        className={`mp-feature-value-cell ${isCurrent ? 'current-plan-column' : ''}`}
                      >
                        {renderFeatureValue(feature.getValue(plan))}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* Footer */}
          <div className="mp-footer-msg">
            <span>Join thousands of businesses already using GulfConstructor to connect, grow, and succeed. Your membership isn't just a plan — it's a partnership for smarter trade.</span>
          </div>
        </div>

        {/* Verification Badges */}
        <div className="mp-badges-section">
          <div className="mp-badges-header">
            <h2>Verification Badges (Optional Add-ons)</h2>
            <p>Boost your credibility and business - a trusted badge level that matches your trade goals.</p>
          </div>

          <div className="mp-badges-grid">
            {displayBadges.map((badge) => {
              const isOwned = isUserBadgeActive(badge.name);

              return (
                <div key={badge.id} className={`mp-badge-card ${badge.name}`}>
                  <div className={`mp-badge-icon-wrapper ${badge.name}`}>
                    {badge.name === 'verified' && (
                      <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M24 4L28.0556 8.72727L34.2857 7.27273L35.1429 13.6364L41.1429 16L38.5714 22L41.1429 28L35.1429 30.3636L34.2857 36.7273L28.0556 35.2727L24 40L19.9444 35.2727L13.7143 36.7273L12.8571 30.3636L6.85714 28L9.42857 22L6.85714 16L12.8571 13.6364L13.7143 7.27273L19.9444 8.72727L24 4Z" fill="#009D0A"/>
                        <path d="M17 22L21.5 26.5L31 17" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                    {badge.name === 'inspected' && (
                      <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="22" cy="22" r="10" stroke="#FFAE00" strokeWidth="3"/>
                        <path d="M29 29L40 40" stroke="#FFAE00" strokeWidth="3" strokeLinecap="round"/>
                        <path d="M18 22L21 25L27 19" stroke="#FFAE00" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                    {badge.name === 'certified' && (
                      <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M24 4L28.5 15.5H40.5L31 23L35 35L24 27.5L13 35L17 23L7.5 15.5H19.5L24 4Z" fill="#1F396C"/>
                        <circle cx="24" cy="21" r="6" fill="white"/>
                        <path d="M21 21L23 23L27 19" stroke="#1F396C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                  </div>
                  <h3 className="mp-badge-title">{badge.displayName}</h3>
                  <div className="mp-badge-price">
                    <span className="mp-badge-amount">{formatPrice(badge.price)}</span>
                    <span className="mp-badge-period">/year</span>
                  </div>
                  <p className="mp-badge-desc">{badge.description}</p>
                  <button
                    className={`mp-badge-btn ${badge.name} ${isOwned ? 'active' : ''}`}
                    onClick={() => !isOwned && handlePurchaseBadge(badge.id)}
                    disabled={isOwned || purchasingBadge}
                  >
                    {isOwned ? 'Active' : badge.name === 'verified' ? 'Get Verified' : badge.name === 'inspected' ? 'Register for Inspected' : 'Upgrade to Certified'}
                  </button>
                </div>
              );
            })}
          </div>

          <p className="mp-badges-footer">Businesses help increase buyer trust on the platform.</p>
        </div>

        {/* Modals */}
        <VerificationBadgeModal
          isOpen={showVerificationModal}
          onClose={handleCloseModals}
          badge={selectedBadge}
          onSubmit={handleVerificationSubmit}
        />
        <PaymentGatewayModal
          isOpen={showPaymentModal}
          onClose={handleCloseModals}
          badge={selectedBadge}
          onConfirm={handlePaymentConfirm}
          type="badge"
        />
      </div>
    </SpecialistDashboardLayout>
  );
};

export default SpecialistMembershipPage;
